import { CompanyParametersQuery, CurrencyCollectionQuery, propertyChangedJoinPointFilter, VatRateForPurchaseQuery, VatTableItem, VatTableItemContext, VatTableItemGrossRecalculation, VatTableItemNetRecalculation } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, ObservableCollection, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, PurchaseExpenseDocumentModel, PurchaseVatTableItemModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { PurchaseEmptyExpenseNameError } from '../violations/purchase-empty-expense-name-error';
import { PurchaseIncorrectNetValueWarning } from '../violations/purchase-incorrect-net-value-warning';
import { PurchaseIncorrectVatValueWarning } from '../violations/purchase-incorrect-vat-value-warning';


interface PurchaseModelPart extends ModelBase {
    currencyId: number;
    vatTable: ObservableCollection<PurchaseVatTableItemModel>;
    exchangeRate: ExchangeRateModel;
    calculationMethod: CalculationMethod;
}

export class PurchaseExpenseDocumentValidationBOPart extends BusinessObjectPart<PurchaseModelPart> {
    private readonly vatRateNetCalculator: VatTableItemNetRecalculation;
    private readonly vatRateGrossCalculator: VatTableItemGrossRecalculation;

    public constructor(
        weaver: Weaver,
        private readonly queryBus: QueryBus) {
        super(weaver);
        this.vatRateNetCalculator = new VatTableItemNetRecalculation();
        this.vatRateGrossCalculator = new VatTableItemGrossRecalculation();
    }

    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerValidationRules();
    }

    private registerValidationRules(): void {
        this.registerNotEmptyExpenseNameRule();
        this.registerIncorrectVatValueRule();
        this.registerIncorrectNetValueRule();
        this.registerReadonlyRules();
    }

    private get exchangeRateSet(): boolean {
        return this.bo.model.exchangeRate !== null;
    }

    private get inForeignCurrency(): boolean {
        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const pln = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
        return this.bo.model.currencyId !== pln.id;
    }

    private registerReadonlyRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod', 'currencyId'])(
            () => {
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'vatValue']);
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'grossValue']);
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'netValue']);
            }
        )));
        this.engine.readonly.addRule(
            [[PurchaseVatTableItemModel, 'vatValue'], [PurchaseVatTableItemModel, 'manual']],
            [[PurchaseVatTableItemModel, 'vatValue']],
            ctx => !ctx.source.manual
        );

        this.engine.readonly.addRule(
            [[PurchaseVatTableItemModel, 'grossValue'], [PurchaseVatTableItemModel, 'manual']],
            [[PurchaseVatTableItemModel, 'grossValue']],
            ctx => !ctx.source.manual && (this.bo.model.calculationMethod !== CalculationMethod.Gross || this.inForeignCurrency)
        );

        this.engine.readonly.addRule(
            [[PurchaseVatTableItemModel, 'netValue'], [PurchaseVatTableItemModel, 'manual']],
            [[PurchaseVatTableItemModel, 'netValue']],
            ctx => !ctx.source.manual && (this.bo.model.calculationMethod !== CalculationMethod.Net || this.inForeignCurrency)
        );

        this.engine.readonlyChecker.addOmitRule([
            [PurchaseVatTableItemModel, 'vatValue'],
            [PurchaseVatTableItemModel, 'grossValue'],
            [PurchaseVatTableItemModel, 'netValue']
        ], () => true);
    }

    private convertVatTableItemModelToItem(vatTableItemModel: PurchaseVatTableItemModel, rate: number, itemContext: VatTableItemContext): VatTableItem {
        const convertedItem: VatTableItem = {
            vatRate: rate,
            vatRateId: vatTableItemModel.vatRateId,
            vatValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyVatValue : vatTableItemModel.vatValue,
            netValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyNetValue : vatTableItemModel.netValue,
            grossValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyGrossValue : vatTableItemModel.grossValue,
        };
        return convertedItem;
    }

    private calculateSystemCurrencyItemFromDocumentCurrencyItem(item: PurchaseVatTableItemModel): VatTableItem {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
        const { rate } = this.bo.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: item.vatRateId });
        const multiplier = this.bo.model.exchangeRate.rate / this.bo.model.exchangeRate.units;
        const convertedItem: VatTableItem = {
            vatRate: rate,
            vatRateId: item.vatRateId,
            vatValue: item.currencyVatValue,
            netValue: Calc.round(item.currencyNetValue! * multiplier, precision, MidpointRounding.AwayFromZero),
            grossValue: Calc.round(item.currencyGrossValue! * multiplier, precision, MidpointRounding.AwayFromZero)
        };
        return this.vatRateNetCalculator.recalculate(convertedItem, precision, 'net');
    }

    private registerNotEmptyExpenseNameRule(): void {
        this.engine.validation.addRule(
            [[PurchaseExpenseDocumentModel, 'expenseName']],
            ctx => !!(ctx.source.expenseName || '').trim(),
            PurchaseEmptyExpenseNameError
        );
    }

    private registerIncorrectVatValueRule(): void {
        this.engine.validation.addRule(
            [[PurchaseVatTableItemModel, 'vatValue'], [PurchaseVatTableItemModel, 'netValue'], [PurchaseVatTableItemModel, 'grossValue'], [PurchaseVatTableItemModel, 'currencyNetValue']],
            [[PurchaseVatTableItemModel, 'vatValue']],
            ctx => {
                const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
                const { rate } = this.bo.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: ctx.source.vatRateId });

                const correctItemFromNet = this.vatRateNetCalculator.recalculate(this.convertVatTableItemModelToItem(ctx.source, rate, VatTableItemContext.SystemCurrency), precision, 'net');
                const correctItemFromGross = this.vatRateGrossCalculator.recalculate(this.convertVatTableItemModelToItem(ctx.source, rate, VatTableItemContext.SystemCurrency), precision, 'gross');
                const isCorrectFromNet = correctItemFromNet.vatValue === ctx.source.vatValue && correctItemFromNet.netValue === ctx.source.netValue && correctItemFromNet.grossValue === ctx.source.grossValue;
                const isCorrectFromGross = correctItemFromGross.vatValue === ctx.source.vatValue && correctItemFromGross.netValue === ctx.source.netValue && correctItemFromGross.grossValue === ctx.source.grossValue;
                return isCorrectFromGross || isCorrectFromNet;
            },
            PurchaseIncorrectVatValueWarning
        );
    }

    private registerIncorrectNetValueRule(): void {
        this.engine.validation.addRule(
            [[PurchaseVatTableItemModel, 'currencyNetValue'], [PurchaseVatTableItemModel, 'netValue']],
            [[PurchaseVatTableItemModel, 'netValue']],
            ctx => {
                if (this.exchangeRateSet && this.inForeignCurrency) {
                    const itemToCalculate = this.bo.model.vatTable.find(item => item.vatRateId === ctx.source.vatRateId);
                    const systemCurrencyCalculatedItem = this.calculateSystemCurrencyItemFromDocumentCurrencyItem(ctx.source);
                    return systemCurrencyCalculatedItem.netValue === itemToCalculate?.netValue;
                }
                return true;
            },
            PurchaseIncorrectNetValueWarning
        );
    }
}
